/*! Copyright (c) 2021, XAPPmedia */

import { WidgetEnv } from "@xapp/stentor-chat-widget";
import React, { FC } from "react";
import ReactDOM from "react-dom";
import ChatWidgetContainer from "./components/ChatWidget/ChatWidgetContainer";
import { parseEnv } from "./utils/parseEnv";
import useFetchStylesheet from "./utils/useFetchStylesheet";
import { err } from "./utils";

declare global {
    interface Window {
        xaConfig?: WidgetEnv;
        xaLogLevel?: "debug";
    }
}

export interface WindowWithConfig extends Window {
    readonly xaConfig?: WidgetEnv;
}

/**
 * An external script could have replaced the config on the window.
 * 
 * @param config 
 */
export function injectConfig(config?: string) {
    // The external script will look for 
    const injected = config || "{{INJECT}}";

    // The way we test to see if it was injected is by JSON.parse
    try {
        const config = JSON.parse(injected);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore Not sure of a better way to add this for TypeScript
        const existingConfig = window.xaConfig || {} as Record<string, any>;
        window.xaConfig = { ...config, ...existingConfig, attributes: { ...config.attributes, ...existingConfig.attributes } };
    } catch (e) {
        err(`Unable to load widget config: ${e}`);
    }
}

function getConfigFromWindow(): Partial<WidgetEnv> {
    if (hasConfig(window)) {
        return window.xaConfig;
    }
    return {};
}

function getConfig(): WidgetEnv {
    const env = parseEnv<WidgetEnv>(process.env);

    const locationUrl = new URL(window.location.href);
    const isAdmin = locationUrl.searchParams.get("isAdmin") === "true";

    const windowConfig = getConfigFromWindow();

    const config: WidgetEnv = {
        ...env,
        isAdmin,
        ...windowConfig,
        attributes: {
            ...env?.attributes,
            ...windowConfig?.attributes
        }
    };

    return config;

}

function hasConfig(wd: Window): wd is WindowWithConfig {
    return !!wd && typeof (wd as WindowWithConfig).xaConfig === "object";
}

export interface AppProps {
    readonly env: WidgetEnv;
}

const App: FC<AppProps> = props => {
    const { env } = props;
    const [loaded] = useFetchStylesheet();

    if (env.disabled) {
        return <></>;
    }

    return loaded ? <ChatWidgetContainer config={env} /> : <></>;
}

function renderWidget() {
    let widget = document.getElementById("xapp-widget");

    if (!widget) {
        widget = document.createElement("div");
        widget.id = "xapp-widget";
        document.body.appendChild(widget);
    }
    // See if the config was injected on fetch, otherwise it will do nothing
    injectConfig();

    // Pull the config off of window.xaConfig
    const config = getConfig();

    ReactDOM.render(<App env={config} />, widget);
}

window.addEventListener("load", () => {
    renderWidget();
});

// when the script is loaded after the page has already loaded
if (document.readyState === "complete") {
    renderWidget();
}

export { default as React } from "react";
export { ChatMessagePart, ChatMessagePartProps } from "./components/ChatMessagePart";
